import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const C4_COLS = 7;
export const C4_ROWS = 6;

export type Connect4Cell = PlayerIndex | null;

export interface Connect4State extends BaseState {
	kind: "connect4";
	/** Row-major, row 0 is the top. */
	board: Connect4Cell[];
	/** Where the last disc landed, for the drop animation. */
	lastDrop: number | null;
	winningLine: number[] | null;
}

export interface Connect4Move {
	column: number;
}

const at = (col: number, row: number): number => row * C4_COLS + col;

const DIRECTIONS: readonly (readonly [number, number])[] = [
	[1, 0],
	[0, 1],
	[1, 1],
	[1, -1],
];

function findWinThrough(board: Connect4Cell[], col: number, row: number): number[] | null {
	const player = board[at(col, row)];
	if (player === null) return null;

	for (const [dc, dr] of DIRECTIONS) {
		const line = [at(col, row)];
		for (const sign of [1, -1]) {
			let c = col + dc * sign;
			let r = row + dr * sign;
			while (
				c >= 0 &&
				c < C4_COLS &&
				r >= 0 &&
				r < C4_ROWS &&
				board[at(c, r)] === player &&
				line.length < 8
			) {
				line.push(at(c, r));
				c += dc * sign;
				r += dr * sign;
			}
		}
		if (line.length >= 4) return line;
	}
	return null;
}

function lowestEmptyRow(board: Connect4Cell[], column: number): number | null {
	for (let row = C4_ROWS - 1; row >= 0; row--) {
		if (board[at(column, row)] === null) return row;
	}
	return null;
}

export const connect4: GameDefinition<Connect4State, Connect4Move> = {
	id: "connect4",
	category: "classics",
	title: "Connect Four",
	emoji: "🔴",
	blurb: "Drop your discs and line up four before your friend does.",
	howTo: [
		"Take turns dropping a disc into any column.",
		"Discs fall to the lowest free slot.",
		"Four in a row — across, down or diagonal — wins.",
	],

	create(rng) {
		return {
			kind: "connect4",
			board: Array<Connect4Cell>(C4_COLS * C4_ROWS).fill(null),
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
			lastDrop: null,
			winningLine: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const column = (raw as { column?: unknown }).column;
		if (typeof column !== "number" || !Number.isInteger(column)) return null;
		if (column < 0 || column >= C4_COLS) return null;
		return { column };
	},

	apply(state, player, move): MoveOutcome<Connect4State> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const row = lowestEmptyRow(state.board, move.column);
		if (row === null) return reject("That column is full — try another one.");

		const board = [...state.board];
		const index = at(move.column, row);
		board[index] = player;

		const winningLine = findWinThrough(board, move.column, row);
		if (winningLine) {
			const scores: [number, number] = [0, 0];
			scores[player] = 1;
			return {
				ok: true,
				state: {
					...state,
					board,
					scores,
					turn: null,
					result: { winner: player },
					lastDrop: index,
					winningLine,
				},
			};
		}

		if (board.every((cell) => cell !== null)) {
			return {
				ok: true,
				state: {
					...state,
					board,
					turn: null,
					result: { winner: null },
					lastDrop: index,
				},
			};
		}

		return {
			ok: true,
			state: { ...state, board, turn: otherPlayer(player), lastDrop: index },
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: Connect4Move[] = [];
		for (let column = 0; column < C4_COLS; column++) {
			if (lowestEmptyRow(state.board, column) !== null) moves.push({ column });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
